import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { XCircle, MagicWand, Check, Warning, ArrowsClockwise } from "@phosphor-icons/react";

/**
 * Phase 13 Beat 1 · Add Job dialog.
 *
 * UAT Bug 2 (Feb 2026) — Validate URL previously failed with a generic
 * "Something went wrong". We now surface the specific 422 detail from
 * `POST /api/jobs` AND auto-switch to Manual Entry, preserving the URL so
 * the Executive doesn't lose typed input.
 *
 * UAT Bug 4 (Feb 2026) — Manual Job Entry felt too slow. We now show a
 * granular progress phase ("Hashing JD → Detecting duplicates → Saving
 * snapshot") so the Executive can see the pipeline advancing instead of
 * staring at a static "Saving…". No integrity checks are removed.
 */
export default function AddJobDialog({ onClose, onCreated }) {
  const [step, setStep] = useState("url"); // url → preview → manual (fallback)
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("");   // "fetching" | "extracting" | "hashing" | "duplicate" | "snapshot"
  const [preview, setPreview] = useState(null);
  const [duplicate, setDuplicate] = useState(null);
  const [urlError, setUrlError] = useState(null);   // inline hint after auto-switch to manual
  const [manual, setManual] = useState({
    job_title: "", company: "", location: "", jd_text: "",
    work_arrangement: "", employment_type: "", source: "Manual Entry",
  });

  // Refetch the parent list on close when a duplicate was seen so the incremented
  // reuse_count is visible immediately without the user hitting Refresh.
  const handleClose = () => { if (duplicate) onCreated(); else onClose(); };

  const phaseLabel = () => ({
    fetching:  "Fetching the job page…",
    extracting: "Reading the JD with Claude…",
    hashing:   "Hashing the JD…",
    duplicate: "Checking the library for duplicates…",
    snapshot:  "Saving the JD snapshot…",
  }[phase] || "Saving…");

  const extractAndCreate = async () => {
    if (!url.trim()) { toast.error("Please paste a Job URL."); return; }
    setBusy(true); setDuplicate(null); setPreview(null); setUrlError(null);
    setPhase("fetching");
    // Nudge the phase forward so the user sees the pipeline is running.
    // The backend does fetch → LLM → hash → dup-check sequentially; we can't
    // hook per-step callbacks over HTTP so we approximate for UX only.
    const advance = setInterval(() => {
      setPhase((p) => (p === "fetching" ? "extracting" :
                       p === "extracting" ? "duplicate" : p));
    }, 1200);
    try {
      const { data } = await api.post("/jobs", { job_url: url });
      if (data.duplicate) {
        setDuplicate(data.job);
        toast.info("This job is already in the library — showing existing record.");
        return;
      }
      setPreview(data.job);
      setStep("preview");
    } catch (ex) {
      const status = ex.response?.status;
      const detail = ex.response?.data?.detail;
      // UAT Bug 2 — 422 with our structured extraction-failed payload:
      // surface the friendly message inline AND flip to manual entry so the
      // Executive can immediately paste the details.
      if (status === 422 && detail && typeof detail === "object" && detail.code === "url_extraction_failed") {
        setUrlError(detail.message || "Extraction failed.");
        toast.error(detail.message || "URL extraction failed.");
        // Keep the URL — many pages copy their title/company into the URL slug,
        // which the Executive may want to reference when filling out fields.
        setStep("manual");
      } else {
        toast.error(formatApiError(detail) || "Failed to add job");
      }
    } finally {
      clearInterval(advance);
      setBusy(false); setPhase("");
    }
  };

  const submitManual = async () => {
    if (!manual.job_title || !manual.company) {
      toast.error("Job title and company are required for manual entry.");
      return;
    }
    setBusy(true); setDuplicate(null); setUrlError(null);
    setPhase("hashing");
    const advance = setInterval(() => {
      setPhase((p) => (p === "hashing" ? "duplicate" :
                       p === "duplicate" ? "snapshot" : p));
    }, 600);
    try {
      const { data } = await api.post("/jobs", { ...manual, job_url: url || null });
      if (data.duplicate) {
        setDuplicate(data.job);
        toast.info("Duplicate detected — existing job shown.");
        return;
      }
      toast.success("Job added to the library");
      onCreated();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Failed to add job");
    } finally {
      clearInterval(advance);
      setBusy(false); setPhase("");
    }
  };

  const confirmPreview = () => {
    toast.success("Job saved to the library");
    onCreated();
  };

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4"
         data-testid="add-job-dialog">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl">
        <div className="p-6 border-b border-slate-100 flex items-start justify-between">
          <div>
            <h3 className="text-xl font-semibold text-slate-900">Add Job to Library</h3>
            <p className="text-xs text-slate-500 mt-1">
              Paste a Job URL and we'll extract the title, company, location, JD and skills automatically.
              If extraction fails, you can fall back to manual entry.
            </p>
          </div>
          <button onClick={handleClose} data-testid="add-job-close" className="text-slate-400 hover:text-slate-700"><XCircle size={20} /></button>
        </div>

        <div className="p-6 space-y-4">
          {duplicate && (
            <div className="border border-indigo-200 bg-indigo-50 rounded-lg p-3 text-sm text-indigo-900"
                 data-testid="add-job-duplicate-banner">
              <div className="font-medium flex items-center gap-2"><Warning size={14} /> This job is already in the library.</div>
              <div className="text-xs mt-1">
                <span className="font-medium">{duplicate.job_title}</span> · {duplicate.company} · {duplicate.location}
              </div>
              <div className="text-[11px] text-indigo-700 mt-1">Discovered by {duplicate.discovered_by_name || "unknown"} · reuse counter incremented.</div>
            </div>
          )}

          {step === "url" && !duplicate && (
            <>
              <div>
                <div className="text-xs font-semibold text-slate-700 mb-1.5">Job URL</div>
                <input value={url} onChange={(e) => setUrl(e.target.value)}
                       data-testid="add-job-url"
                       placeholder="https://company.com/careers/business-data-analyst-123"
                       className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/40" />
                <p className="text-[11px] text-slate-500 mt-1">
                  We'll fetch the page and use Claude Sonnet 4.6 to structure the JD, skills, salary, and location.
                </p>
              </div>
              {busy && (
                <div className="border border-sky-200 bg-sky-50 rounded-lg p-3 text-xs text-sky-800 inline-flex items-center gap-2"
                     data-testid="add-job-phase">
                  <ArrowsClockwise size={13} className="animate-spin" /> {phaseLabel()}
                </div>
              )}
              <div className="flex justify-between">
                <button onClick={() => setStep("manual")}
                        data-testid="add-job-goto-manual"
                        className="text-xs text-slate-500 hover:text-slate-700 underline">
                  Or enter Job details manually
                </button>
                <button onClick={extractAndCreate} disabled={busy || !url}
                        data-testid="add-job-extract"
                        className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium py-2 px-4 disabled:opacity-50">
                  {busy ? "Extracting…" : (<><MagicWand size={14} /> Extract & Add</>)}
                </button>
              </div>
            </>
          )}

          {step === "preview" && preview && (
            <div className="space-y-4" data-testid="add-job-preview">
              <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-3 text-xs text-emerald-800 flex items-center gap-2">
                <Check size={14} /> Extraction complete — review and confirm below.
              </div>
              <PreviewRow label="Job title" value={preview.job_title} />
              <PreviewRow label="Company" value={preview.company} />
              <PreviewRow label="Location" value={preview.location} />
              <PreviewRow label="Arrangement" value={preview.work_arrangement} />
              <PreviewRow label="Employment type" value={preview.employment_type} />
              <PreviewRow label="Required skills" value={(preview.required_skills || []).join(", ")} />
              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-medium">JD snapshot fingerprint</div>
                <div className="text-xs text-slate-700 font-mono break-all" data-testid="add-job-preview-hash">
                  {preview.jd_hash || "—"}
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => { setStep("url"); setPreview(null); }}
                        className="text-sm px-3 py-2 rounded border border-slate-200">Add another</button>
                <button onClick={confirmPreview}
                        data-testid="add-job-preview-confirm"
                        className="text-sm px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white">Done</button>
              </div>
            </div>
          )}

          {step === "manual" && (
            <div className="space-y-3" data-testid="add-job-manual">
              {urlError && (
                <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 text-xs text-amber-900"
                     data-testid="add-job-url-error">
                  <div className="font-medium flex items-center gap-2">
                    <Warning size={13} weight="fill" /> URL extraction couldn't complete
                  </div>
                  <div className="mt-1">{urlError}</div>
                  <div className="mt-1 text-amber-800">
                    Paste the job details manually below. The original URL is preserved — we'll store it for reference.
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <TextInput label="Job title *" value={manual.job_title} onChange={(v) => setManual((m) => ({ ...m, job_title: v }))} testid="manual-title" />
                <TextInput label="Company *" value={manual.company} onChange={(v) => setManual((m) => ({ ...m, company: v }))} testid="manual-company" />
                <TextInput label="Location" value={manual.location} onChange={(v) => setManual((m) => ({ ...m, location: v }))} testid="manual-location" />
                <TextInput label="Work arrangement" value={manual.work_arrangement} onChange={(v) => setManual((m) => ({ ...m, work_arrangement: v }))} testid="manual-arrangement" placeholder="Remote / Hybrid / On-site" />
                <TextInput label="Employment type" value={manual.employment_type} onChange={(v) => setManual((m) => ({ ...m, employment_type: v }))} testid="manual-employment" />
                <TextInput label="Source" value={manual.source} onChange={(v) => setManual((m) => ({ ...m, source: v }))} testid="manual-source" />
                <label className="block">
                  <div className="text-xs font-semibold text-slate-700 mb-1.5">Originally posted date (optional)</div>
                  <input type="date"
                          value={(manual.originally_posted_at || "").slice(0, 10)}
                          onChange={(e) => setManual((m) => ({ ...m, originally_posted_at: e.target.value }))}
                          data-testid="manual-posted-at"
                          className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
                  <p className="text-[11px] text-slate-500 mt-0.5">Employer's date — leave blank if unknown. Never inferred.</p>
                </label>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-700 mb-1.5">Job description *</div>
                <textarea value={manual.jd_text} onChange={(e) => setManual((m) => ({ ...m, jd_text: e.target.value }))}
                          rows={6}
                          data-testid="manual-jd"
                          placeholder="Paste the job description. This becomes the immutable JD snapshot."
                          className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
                <p className="text-[11px] text-slate-500 mt-1">
                  Manual JD fallback — use when the public page blocks automated capture.
                </p>
              </div>
              {busy && (
                <div className="border border-sky-200 bg-sky-50 rounded-lg p-3 text-xs text-sky-800 inline-flex items-center gap-2"
                     data-testid="add-job-manual-phase">
                  <ArrowsClockwise size={13} className="animate-spin" /> {phaseLabel()}
                </div>
              )}
              <div className="flex justify-between">
                <button onClick={() => setStep("url")} className="text-xs text-slate-500 hover:text-slate-700 underline">
                  Back to URL
                </button>
                <button onClick={submitManual} disabled={busy}
                        data-testid="add-job-manual-submit"
                        className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium py-2 px-4 disabled:opacity-50">
                  {busy ? phaseLabel() : "Save Job"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewRow({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-medium">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5">{value || "—"}</div>
    </div>
  );
}

function TextInput({ label, value, onChange, testid, placeholder }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-slate-700">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)}
             data-testid={testid} placeholder={placeholder}
             className="w-full mt-1 border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/40" />
    </label>
  );
}
